// src/pages/finance-portal/balance-history/BalanceHistory.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Skeleton,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(amount, currency = 'USD') {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
    }).format(amount);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function formatDateTime(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function TrendArrow({ current, previous }) {
    if (current == null || previous == null) {
        return <RemoveIcon sx={{ fontSize: 16, color: '#94a3b8' }} />;
    }
    if (current > previous) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <ArrowUpwardIcon sx={{ fontSize: 16, color: ACCENT }} />
                <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: ACCENT }}>
                    {formatCurrency(current - previous)}
                </Typography>
            </Box>
        );
    }
    if (current < previous) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <ArrowDownwardIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#ef4444' }}>
                    {formatCurrency(previous - current)}
                </Typography>
            </Box>
        );
    }
    return <RemoveIcon sx={{ fontSize: 16, color: '#94a3b8' }} />;
}

export default function BalanceHistory() {
    const { enqueueSnackbar } = useSnackbar();

    const [accounts, setAccounts] = useState([]);
    const [snapshots, setSnapshots] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [snapshotting, setSnapshotting] = useState(false);

    // Filters
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchAccounts = useCallback(async () => {
        setAccountsLoading(true);
        try {
            const response = await axiosInstance.get('/api/finance/stripe/accounts');
            if (response.data.success) {
                const accts = response.data.data || [];
                setAccounts(accts);
                if (accts.length > 0 && !selectedAccountId) {
                    setSelectedAccountId(accts[0].id);
                }
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to load accounts', { variant: 'error' });
        } finally {
            setAccountsLoading(false);
        }
    }, [enqueueSnackbar, selectedAccountId]);

    const fetchSnapshots = useCallback(async () => {
        if (!selectedAccountId) return;
        setIsLoading(true);
        try {
            const params = {};
            if (dateFrom) params.from_date = dateFrom;
            if (dateTo) params.to_date = dateTo;

            const response = await axiosInstance.get(`/api/finance/stripe/balance-snapshots/${selectedAccountId}`, { params });
            if (response.data.success) {
                setSnapshots(response.data.data || []);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to load balance history', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [selectedAccountId, dateFrom, dateTo, enqueueSnackbar]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    useEffect(() => {
        fetchSnapshots();
    }, [fetchSnapshots]);

    const handleTakeSnapshot = async () => {
        setSnapshotting(true);
        try {
            const response = await axiosInstance.post('/api/finance/stripe/balance-snapshots/take-all', {});
            if (response.data.success) {
                enqueueSnackbar('Balance snapshots captured', { variant: 'success' });
                fetchSnapshots();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to take snapshot', { variant: 'error' });
        } finally {
            setSnapshotting(false);
        }
    };

    const selectedAccount = accounts.find(a => a.id === selectedAccountId);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ShowChartIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Balance History
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Track account balance trends over time
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={snapshotting ? <CircularProgress size={16} color="inherit" /> : <CameraAltIcon />}
                        onClick={handleTakeSnapshot}
                        disabled={snapshotting}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        {snapshotting ? 'Capturing...' : 'Take Snapshot'}
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Filters */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 2.5, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <FilterListIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#64748b' }}>Filters</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
                            <InputLabel sx={{ fontFamily }}>Account</InputLabel>
                            <Select
                                value={selectedAccountId}
                                label="Account"
                                onChange={(e) => setSelectedAccountId(e.target.value)}
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {accountsLoading ? (
                                    <MenuItem value="" disabled sx={{ fontFamily }}>Loading accounts...</MenuItem>
                                ) : accounts.length === 0 ? (
                                    <MenuItem value="" disabled sx={{ fontFamily }}>No accounts available</MenuItem>
                                ) : (
                                    accounts.map(a => (
                                        <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>
                                            {a.name} {a.mask ? `****${a.mask}` : ''}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            type="date"
                            label="From Date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="To Date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ fontFamily, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily } }}
                        />
                    </Box>
                </Paper>

                {/* Current Balance Summary */}
                {selectedAccount && (
                    <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                    Current Balance
                                </Typography>
                                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.8rem', color: '#1e293b' }}>
                                    {formatCurrency(selectedAccount.current_balance, selectedAccount.iso_currency_code)}
                                </Typography>
                            </Box>
                            {selectedAccount.available_balance != null && (
                                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                        Available Balance
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.4rem', color: '#64748b' }}>
                                        {formatCurrency(selectedAccount.available_balance, selectedAccount.iso_currency_code)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                )}

                {/* Snapshot Table */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Date', 'Current Balance', 'Trend', 'Available Balance', 'Trend'].map((h, i) => (
                                        <TableCell key={i} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(6)].map((_, i) => (
                                        <TableRow key={i}>
                                            {[...Array(5)].map((_, j) => (
                                                <TableCell key={j}><Skeleton variant="text" sx={{ fontFamily }} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : !selectedAccountId ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                                            <ShowChartIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>Select an account to view balance history</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : snapshots.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                                            <ShowChartIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No balance snapshots found</Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>Click "Take Snapshot" to capture current balances</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    snapshots.map((snap, index) => {
                                        const prevSnap = index < snapshots.length - 1 ? snapshots[index + 1] : null;

                                        return (
                                            <TableRow key={snap.id || index} sx={{ '&:hover': { bgcolor: '#f0fdf4' } }}>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                                                    {formatDateTime(snap.snapshot_date || snap.created_at)}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontFamily, fontWeight: 700, fontSize: '0.9rem',
                                                    color: (snap.current_balance || 0) >= 0 ? '#1e293b' : '#ef4444',
                                                    borderBottom: '1px solid #f1f5f9',
                                                }}>
                                                    {formatCurrency(snap.current_balance)}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <TrendArrow
                                                        current={snap.current_balance}
                                                        previous={prevSnap?.current_balance}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#64748b',
                                                    borderBottom: '1px solid #f1f5f9',
                                                }}>
                                                    {formatCurrency(snap.available_balance)}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <TrendArrow
                                                        current={snap.available_balance}
                                                        previous={prevSnap?.available_balance}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
}
